import { getBootstrap } from '@client/app/bootstrap';

/**
 * @typedef {Object} EditBizcardParams
 * @property {string} template - The bizcard template
 * @property {string} [element] - Optional specific element to edit
 * @property {string} prompt - The AI prompt for editing
 * @property {() => void} [onStreamStart] - Optional callback when stream starts
 * @property {AbortSignal} [signal] - Optional abort signal for canceling the request
 */

/**
 * @typedef {Object} EditBizcardResponse
 * @property {string} [template] - The edited template (if successful)
 * @property {string} [error] - Error message (if failed)
 */

/**
 * Edit a bizcard using AI
 * @param {EditBizcardParams} params - The parameters for editing
 * @returns {Promise<EditBizcardResponse>} The response with template or error
 */
export async function editBizcard(params) {
  const { template, element, prompt, signal } = params;
  const { token } = getBootstrap();

  try {
    const response = await fetch(`${import.meta.env.VITE_AI_API}/api/v1/ai/bizcards`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({
        template,
        element,
        prompt
      }),
      signal
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        error: errorData.error || `HTTP error! status: ${response.status}`
      };
    }

    const data = await response.json();
    return {
      template: data.template
    };
  } catch (error) {
    return {
      error: error instanceof Error ? error.message : 'An unknown error occurred'
    };
  }
}

/**
 * Check if AI bizcards feature is enabled
 * @returns {Promise<boolean>} True if enabled, false otherwise
 */
export async function isEnabled() {
  const { token } = getBootstrap();

  try {
    const response = await fetch(`${import.meta.env.VITE_AI_API}/api/v1/ai/bizcards`, {
      method: 'HEAD',
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.status < 400;
  } catch {
    return false;
  }
}
